import { useReactiveVar } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import React, { type ReactElement, useContext } from 'react';

import { HOME_PATH } from 'src/constants';
import { CollectionSimple } from 'src/dynamoQueries/collections';
import { exitEditMode, getCurrIdxByItemId } from 'src/helpers';
import { useBuildQuery } from 'src/hooks/build';
import { SharedLinkContext } from 'src/SharedLinkPage/context';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';

// Search params interface - replaces location.state
interface MediaPageSearchParams {
  collectionId?: string;
  collectionType?: 'GALLERY' | 'PLAYLIST';
  fromLibrary?: boolean;
}

interface MediaPageChildProps {
  onFwd?: () => void;
  onBack?: () => void;
  fwdDisabled?: boolean;
  backDisabled?: boolean;
  showNavButtons?: boolean;
  itemId?: string;
}

interface BaseNavigationWrapperProps {
  children: ReactElement<MediaPageChildProps>;
  itemIDs: string[];
  basePathname?: string | null;
}

interface CollectionNavigationWrapperProps {
  children: ReactElement<MediaPageChildProps>;
  shared?: boolean;
}

interface OpenSearchNavigationWrapperProps {
  children: ReactElement<MediaPageChildProps>;
}

interface CollectionEdge {
  node: {
    contentObject: {
      id: string;
    };
  };
}

interface CollectionData {
  collection?: {
    items?: {
      edges: CollectionEdge[];
    };
  };
  sharedCollection?: {
    items?: {
      edges: CollectionEdge[];
    };
  };
}

// Generic base wrapper component
export const BaseNavigationWrapper = ({
  children,
  itemIDs,
  basePathname = null,
}: BaseNavigationWrapperProps): ReactElement => {
  const params = useParams({ strict: false });
  const clipId = (params as { clipId?: string }).clipId;
  const navigate = useNavigate();
  // Use search params instead of location.state - this survives page refresh
  const searchParams = useSearch({ strict: false }) as MediaPageSearchParams;
  const { collectionId, collectionType } = searchParams || {};

  const getDisabledState = (prev = false): boolean => {
    if (!clipId) return true;
    const currIdx = getCurrIdxByItemId(clipId, itemIDs);
    if ((prev && currIdx === 0) || (!prev && currIdx === itemIDs.length - 1)) {
      return true;
    }
    return false;
  };

  const navigateReplace = (assetId: string): void => {
    const basepath = basePathname || `${HOME_PATH}/media-page/`;

    navigate({
      to: `${basepath}${assetId}`,
      search: {
        collectionId,
        collectionType,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router's generic search type requires runtime flexibility
      } as any,
      replace: true,
    });
  };

  const nextButtonHandler = (): void => {
    if (!clipId) return;
    exitEditMode();
    const idx = getCurrIdxByItemId(clipId, itemIDs);
    const assetId = itemIDs[idx + 1];
    navigateReplace(assetId);
  };

  const previousButtonHandler = (): void => {
    if (!clipId) return;
    exitEditMode();
    const idx = getCurrIdxByItemId(clipId, itemIDs);
    const assetId = itemIDs[idx - 1];
    navigateReplace(assetId);
  };

  return React.cloneElement(children, {
    onFwd: nextButtonHandler,
    onBack: previousButtonHandler,
    fwdDisabled: getDisabledState(),
    backDisabled: getDisabledState(true),
    showNavButtons: !!itemIDs,
    itemId: clipId,
  });
};

// OpenSearch specific wrapper
export const OpenSearchNavigationWrapper = ({ children }: OpenSearchNavigationWrapperProps): ReactElement => {
  const { assetItems } = useReactiveVar(akomiMediaCardGridVar);
  const itemIds = assetItems?.map((res) => res.id) || [];
  return <BaseNavigationWrapper itemIDs={itemIds}>{children}</BaseNavigationWrapper>;
};

export const CollectionNavigationWrapper = ({
  children,
  shared = false,
}: CollectionNavigationWrapperProps): ReactElement => {
  // Use search params instead of location.state
  const searchParams = useSearch({ strict: false }) as MediaPageSearchParams;
  const { collectionId } = searchParams || {};
  const context = useContext(SharedLinkContext);
  const { linkId } = context;
  const { data, loading } = useBuildQuery<CollectionData>(
    CollectionSimple(true, shared) as unknown as Record<string, unknown>,
    {
      variables: {
        id: collectionId,
        fetchPolicy: 'network-only',
      },
    },
  );

  if (loading) {
    return <CircularProgress color="secondary" />;
  }

  const collection = shared ? data?.sharedCollection : data?.collection;

  const itemIDs = collection?.items?.edges.map((edge) => edge.node.contentObject.id) || [];

  const pathname = shared ? `${HOME_PATH}/shared/${linkId}/media-page/` : `${HOME_PATH}/media-page/`;

  return (
    <BaseNavigationWrapper itemIDs={itemIDs} basePathname={pathname}>
      {children}
    </BaseNavigationWrapper>
  );
};
